import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import axios from "axios";
import { fetchStateData, BASE_URL } from "../Services/APIService";

interface viewDatas {
  stateId: number;
  stateName: string;
  isActive: boolean;
  districtId: number;
  districtName: string;
  cityId: number;
  cityName: string;
}

interface UpdateCityCardProps {
  viewData: viewDatas | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface District {
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface FormData {
  cityId: number;
  cityName: string;
  isActive: boolean;
  stateId: number | null;
  districtId: number | null;
}

const UpdateCityCard: React.FC<UpdateCityCardProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    cityId: 0,
    cityName: "",
    isActive: true,
    stateId: null,
    districtId: null,
  });

  const [states, setStates] = useState<State[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const requiredField = <span className="text-danger ms-1">*</span>;

  // Fetch states and districts on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingStates(true);
      setIsLoadingDistricts(true);

      try {
        // Fetch states
        const stateData = await fetchStateData();
        setStates(stateData.filter((state) => state.isActive));

        // Fetch all districts
        await fetchDistricts();
      } catch (error) {
        setError("Failed to fetch data");
        console.error(error);
      } finally {
        setIsLoadingStates(false);
      }
    };

    fetchData();
  }, []);

  // Set form data when viewData changes
  useEffect(() => {
    if (viewData) {
      setFormData({
        cityId: viewData.cityId,
        cityName: viewData.cityName,
        isActive: viewData.isActive,
        stateId: viewData.stateId,
        districtId: viewData.districtId,
      });
    }
  }, [viewData]);

  // Filter districts when state is selected or district data changes
  useEffect(() => {
    if (formData.stateId && allDistricts.length > 0) {
      const districts = allDistricts.filter(
        (district) => district.stateId === formData.stateId
      );

      setFilteredDistricts(districts.filter((dist) => dist.isActive));

      // If current districtId doesn't belong to selected state, reset it
      if (
        formData.districtId &&
        !districts.some((d) => d.districtId === formData.districtId)
      ) {
        setFormData((prev) => ({
          ...prev,
          districtId: districts.length > 0 ? districts[0].districtId : null,
        }));
      }
    } else {
      setFilteredDistricts([]);
    }
  }, [formData.stateId, allDistricts]);

  const fetchDistricts = async () => {
    setIsLoadingDistricts(true);
    const authToken = localStorage.getItem("authToken");

    if (!authToken) {
      throw new Error("Authentication token not found");
    }

    try {
      const response = await axios.get(`${BASE_URL}/Location/GetAllDistricts`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.districtList) {
        setAllDistricts(
          response.data.districtList.filter(
            (dist: { isActive: any }) => dist.isActive
          )
        );
      } else {
        throw new Error("Invalid district data received");
      }
    } catch (error) {
      setError("Failed to fetch districts");
      console.error(error);
    } finally {
      setIsLoadingDistricts(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Handle special case for state change to reset district
    if (name === "stateId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
        districtId: null, // Reset district when state changes
      }));
    } else if (name === "districtId") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const updateCity = async (payload: any) => {
    const authToken = localStorage.getItem("authToken");

    if (!authToken) {
      throw new Error("Authentication token not found");
    }

    const response = await axios.put(
      `${BASE_URL}/Location/UpdateCity`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.cityName.trim()) {
      setError("Please enter a valid City Name");
      return;
    }

    if (!formData.stateId) {
      setError("Please select a State");
      return;
    }

    if (!formData.districtId) {
      setError("Please select a District");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID from localStorage
      const userId = localStorage.getItem("UserId");

      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }

      // Prepare request payload according to the specified format
      const payload = {
        cityID: formData.cityId,
        cityName: formData.cityName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        modifieddBy: parseInt(userId),
      };

      const result = await updateCity(payload);

      if (result.isUpdated) {
        toast.success("City Updated Successfully!");
        // Notify parent component that update is complete
        onUpdateComplete();
      } else {
        toast.error(result.message || "City Updated failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset error state
    setError(null);
    // Call the cancel function
    onCancel();
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update City</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="cityId" className="form-label">
                City ID
              </label>
              <input
                type="number"
                id="cityId"
                name="cityId"
                className="form-control form-control-lg"
                placeholder="City ID"
                value={formData.cityId}
                disabled // Disable editing of City ID
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="stateId" className="form-label">
                State Name
              </label>
              {isLoadingStates ? (
                <div className="text-muted">Loading states...</div>
              ) : (
                <select
                  id="stateId"
                  name="stateId"
                  className="form-select form-control-lg"
                  value={formData.stateId ?? ""}
                  disabled
                  required
                >
                  {states.length === 0 ? (
                    <option value="" disabled>
                      No states available
                    </option>
                  ) : (
                    states.map((state) => (
                      <option key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="districtId" className="form-label">
                District Name
              </label>
              {isLoadingDistricts ? (
                <div className="text-muted">Loading districts...</div>
              ) : (
                <select
                  id="districtId"
                  name="districtId"
                  className="form-select form-control-lg"
                  value={formData.districtId ?? ""}
                  required
                  disabled
                >
                  {filteredDistricts.length === 0 ? (
                    <option value="" disabled>
                      {formData.stateId
                        ? "No districts available for selected state"
                        : "Select a state first"}
                    </option>
                  ) : (
                    filteredDistricts.map((district) => (
                      <option
                        key={district.districtId}
                        value={district.districtId}
                      >
                        {district.districtName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="cityName" className="form-label">
                City Name{requiredField}
              </label>
              <input
                type="text"
                id="cityName"
                name="cityName"
                className="form-control form-control-lg"
                placeholder="City Name"
                value={formData.cityName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <span className="form-check-label">Active</span>
              </label>
            </div>

            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCityCard;
